import { useEffect, useState } from "react";

import { useFormik } from "formik";
import i18next from "i18next";
import { IoCheckmarkCircle } from "react-icons/io5";
import { Link, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useAuth } from "../../../context/auth";
import { applicationRefund, getOrder, userApplicationRefund } from "../../../protocols";

import { DotsLoadingComponent, FileInputComponent, InputComponent, TextareaComponent } from "../../ui";

import {
  Card,
  Fields,
  FormError,
  Links,
  OrderInfo,
  PassengerNote,
  PassengerRow,
  PassengersBlock,
  PassengersHint,
  PassengersLabel,
  Row,
  SubmitButton,
  SubmitButtonLoadingCover,
  Subtitle,
  SuccessIcon,
  Title,
  Wrapper,
} from "./style";

// Типы файла заявления, которые принимает b2c (/application-refund: mimes doc,docx,pdf,jpg,jpeg,png)
const ACCEPTED_FILE_EXTENSIONS = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
const MAX_FILE_SIZE = 64 * 1024 * 1024;

// Ошибки b2c/ads_v1, для которых есть понятный текст (см. Error в b2c и ads_v1)
const ORDER_NOT_FOUND_ERROR = 20;
const ORDER_NOT_FOUND_B2B_ERROR = 52;
const STATUS_NOT_SUITABLE_ERROR = 53;
const REFUND_PASSENGERS_INVALID_ERROR = 73;
const REFUND_INFANT_WITHOUT_ADULT_ERROR = 74;

const fileExtension = file => (file?.name || "").split(".").pop().toLowerCase();

/**
 * Заявка на возврат. Работает и для гостя (номер заказа + email плательщика), и для авторизованного
 * клиента (заказ должен быть его). Со страницы заказов / заказа приходит с ?order_id= (и ?payer_email= для гостя),
 * тогда номер заказа уже заполнен.
 */
const RefundRequestForm = () => {
  const { t } = i18next;
  const { isAuth, user } = useAuth();
  const [searchParams] = useSearchParams();

  const presetOrderId = searchParams.get("order_id") || "";
  const presetEmail = searchParams.get("payer_email") || "";
  const presetSession = searchParams.get("session") || "";

  // возврат по пассажирам: заказ загружается по номеру и сессии (со страницы заказа / списка заказов);
  // без них заявка уходит на всех пассажиров, как раньше
  const [passengers, setPassengers] = useState([]);
  const [selectedPassengers, setSelectedPassengers] = useState([]);

  useEffect(() => {
    if (!presetOrderId || !presetSession) return;

    getOrder({ order_id: presetOrderId, session_id: presetSession, language: i18next.language })
      .then(order => {
        const list = (order?.passenger_data || []).map(p => ({
          id: p.id,
          name: [p.surname, p.name].filter(Boolean).join(" "),
          type: p.type,
          refundStatus: p.refund_status || null,
        }));
        setPassengers(list);
        setSelectedPassengers(list.filter(p => !p.refundStatus).map(p => p.id));
      })
      .catch(() => setPassengers([]));
  }, [presetOrderId, presetSession]);

  const selectablePassengers = passengers.filter(p => !p.refundStatus);

  const togglePassenger = id =>
    setSelectedPassengers(current => (current.includes(id) ? current.filter(v => v !== id) : [...current, id]));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [formError, setFormError] = useState(null);

  const validationSchema = Yup.object().shape({
    order_id: Yup.string().trim().required(t("required")),
    payer_email: isAuth
      ? Yup.string()
      : Yup.string().trim().email(t("enter_valid_mail")).required(t("required")),
    reason: Yup.string().trim().required(t("required")).max(2000),
    phone: Yup.string().trim().max(50),
    requisites: Yup.string().trim().max(1000),
    file: Yup.mixed()
      .required(t("file_required"))
      .test("file-size", t("file_too_large"), file => !file || file.size <= MAX_FILE_SIZE)
      .test("file-type", t("file_wrong_format"), file => !file || ACCEPTED_FILE_EXTENSIONS.includes(fileExtension(file))),
  });

  const handleFormSubmit = async values => {
    if (isSubmitting) return;

    if (selectablePassengers.length > 0 && selectedPassengers.length === 0) {
      setFormError(t("refund_select_passenger_required"));
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    // выбраны не все — заявка только на них (passenger_ids[] — массив в multipart), все — без списка
    const partial = selectablePassengers.length > 0 && selectedPassengers.length < selectablePassengers.length;

    const data = {
      ...(partial ? { "passenger_ids[]": selectedPassengers } : {}),
      order_id: values.order_id.trim(),
      reason: values.reason.trim(),
      phone: values.phone.trim(),
      requisites: values.requisites.trim(),
      file: values.file,
    };

    try {
      const response = isAuth
        ? await userApplicationRefund(data)
        : await applicationRefund({ ...data, payer_email: values.payer_email.trim() });

      const result = response?.data;

      if (result?.status === "success") {
        setIsSent(true);
      } else {
        showError(result?.error_code, result?.error_desc);
      }
    } catch (error) {
      const data = error?.response?.data;
      showError(data?.error_code, data?.error_desc || data?.message || error?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showError = (errorCode, errorDesc) => {
    if (errorCode === ORDER_NOT_FOUND_ERROR || errorCode === ORDER_NOT_FOUND_B2B_ERROR) {
      setFieldError("order_id", t("do_not_find_order_message"));
      return;
    }

    if (errorCode === REFUND_INFANT_WITHOUT_ADULT_ERROR) {
      setFormError(t("refund_infant_without_adult"));
      return;
    }

    if (errorCode === REFUND_PASSENGERS_INVALID_ERROR) {
      setFormError(t("refund_passengers_invalid"));
      return;
    }

    if (errorCode === STATUS_NOT_SUITABLE_ERROR) {
      setFormError(t("refund_status_not_suitable"));
      return;
    }

    const description = typeof errorDesc === "string" ? errorDesc : "";
    setFormError(description ? `${t("error_refund_order_request")} ${description}` : t("error_refund_order_request"));
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, setFieldError } =
    useFormik({
      initialValues: {
        order_id: presetOrderId,
        payer_email: presetEmail || user?.email || "",
        reason: "",
        phone: user?.phone || "",
        requisites: "",
        file: null,
      },
      validationSchema,
      onSubmit: handleFormSubmit,
      validateOnBlur: true,
    });

  if (isSent) {
    return (
      <Wrapper>
        <Card>
          <SuccessIcon>
            <IoCheckmarkCircle />
          </SuccessIcon>
          <Title>{t("success_refund_order_request")}</Title>
          <Subtitle>{t("refund_request_sent_text")}</Subtitle>
          <OrderInfo>
            <span>{t("order_num")}</span>
            <b>{values.order_id}</b>
          </OrderInfo>
          <Links>
            {isAuth && <Link to="/orders">{t("to_orders")}</Link>}
            <Link to="/">{t("to_main")}</Link>
          </Links>
        </Card>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Card>
        <Title>{t("refund_order")}</Title>
        <Subtitle>{t("refund_request_subtitle")}</Subtitle>

        <Fields>
          <Row>
            <InputComponent
              value={values.order_id}
              onChange={handleChange("order_id")}
              onBlur={handleBlur("order_id")}
              label={t("order_number_label")}
              placeholder={t("enter_order_number")}
              touched={touched.order_id}
              error={errors.order_id}
              disabled={!!presetOrderId}
              required
            />

            {!isAuth && (
              <InputComponent
                value={values.payer_email}
                onChange={handleChange("payer_email")}
                onBlur={handleBlur("payer_email")}
                type="email"
                label={t("buyer_email")}
                placeholder={t("enter_buyer_email")}
                touched={touched.payer_email}
                error={errors.payer_email}
                required
              />
            )}

            {isAuth && (
              <InputComponent
                value={values.phone}
                onChange={handleChange("phone")}
                onBlur={handleBlur("phone")}
                type="tel"
                label={`${t("buyer_phone")} (${t("optional_field")})`}
                placeholder="+998 __ ___ __ __"
                touched={touched.phone}
                error={errors.phone}
              />
            )}
          </Row>

          {!isAuth && (
            <InputComponent
              value={values.phone}
              onChange={handleChange("phone")}
              onBlur={handleBlur("phone")}
              type="tel"
              label={`${t("buyer_phone")} (${t("optional_field")})`}
              placeholder="+998 __ ___ __ __"
              touched={touched.phone}
              error={errors.phone}
            />
          )}

          {passengers.length > 1 && (
            <PassengersBlock>
              <PassengersLabel>{t("refund_passengers_label")}</PassengersLabel>
              {passengers.map(passenger => (
                <PassengerRow key={passenger.id} $disabled={!!passenger.refundStatus}>
                  <input
                    type="checkbox"
                    checked={!passenger.refundStatus && selectedPassengers.includes(passenger.id)}
                    disabled={!!passenger.refundStatus}
                    onChange={() => togglePassenger(passenger.id)}
                  />
                  <span>{passenger.name}</span>
                  {passenger.refundStatus && (
                    <PassengerNote>
                      {passenger.refundStatus === "refunded" ? t("refund_passenger_refunded") : t("refund_passenger_in_request")}
                    </PassengerNote>
                  )}
                </PassengerRow>
              ))}
              <PassengersHint>{t("refund_passengers_hint")}</PassengersHint>
            </PassengersBlock>
          )}

          <TextareaComponent
            value={values.reason}
            onChange={handleChange("reason")}
            onBlur={handleBlur("reason")}
            label={t("refund_reason")}
            placeholder={t("enter_refund_reason")}
            touched={touched.reason}
            error={errors.reason}
            maxLength={2000}
            required
          />

          <TextareaComponent
            value={values.requisites}
            onChange={handleChange("requisites")}
            onBlur={handleBlur("requisites")}
            label={`${t("requisite_of_account_to_return")} (${t("optional_field")})`}
            placeholder={t("refund_requisites_hint")}
            touched={touched.requisites}
            error={errors.requisites}
            rows={2}
            maxLength={1000}
          />

          <FileInputComponent
            value={values.file}
            onChange={file => setFieldValue("file", file)}
            onBlur={() => setFieldTouched("file", true, false)}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            label={t("refund_document")}
            hint={t("refund_document_hint")}
            touched={touched.file}
            error={errors.file}
            required
          />
        </Fields>

        {formError && <FormError>{formError}</FormError>}

        <SubmitButton type="button" onClick={handleSubmit} disabled={isSubmitting}>
          {t("send")}
          {isSubmitting && (
            <SubmitButtonLoadingCover>
              <DotsLoadingComponent color="#fff" />
            </SubmitButtonLoadingCover>
          )}
        </SubmitButton>
      </Card>
    </Wrapper>
  );
};

export default RefundRequestForm;
